import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContextualHelp, Heading, Content, Text, ProgressCircle } from '@adobe/react-spectrum';
import Link from '@spectrum-icons/workflow/Link';
import Close from '@spectrum-icons/workflow/Close';
import { importDeckList } from '../../data/redux/slices/importSlice';
import { toast } from 'react-toastify';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { SUPPORTED_SITES, validateDeckImportUrl } from '../../utils/deckImportUrl';
import './SubmitForm.css';

export function SubmitForm(props) {
    const dispatch = useDispatch();
    const [url, setUrl] = useState('');
    const isImporting = useSelector((state) => state?.import?.isImporting);

    const urlCleanedValue = url ? url.trim() : '';
    const isDisabled = !urlCleanedValue || isImporting;

    const handleSubmit = () => {
        if (!urlCleanedValue) return;

        const validationError = validateDeckImportUrl(urlCleanedValue);
        if (validationError) {
            toast(validationError);
            return;
        }

        dispatch(importDeckList(urlCleanedValue));
        try {
            logEvent(getAnalytics(), 'import_start', { url: urlCleanedValue });
        } catch (_err) {
            // analytics is best-effort; never block the import on telemetry
        }
        props?.onStartCallback && props.onStartCallback();
    };

    const handleKeyDown = (evt) => {
        if (evt.key === 'Enter') {
            evt.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className={`submitForm${isImporting ? ' submitForm--busy' : ''}`}>
            <div className="submitFormInputWrap">
                <span className="submitFormInputIcon" aria-hidden="true">
                    <Link size="S" />
                </span>
                <input
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    className="submitFormInput"
                    placeholder="https://www.moxfield.com/decks/…"
                    value={url}
                    onChange={(evt) => setUrl(evt.target.value)}
                    onFocus={(evt) => evt.target.select()}
                    onKeyDown={handleKeyDown}
                    aria-label="Decklist URL"
                    disabled={isImporting}
                />
                {url && !isImporting && (
                    <button
                        type="button"
                        className="submitFormClear"
                        onClick={() => setUrl('')}
                        aria-label="Clear URL"
                        title="Clear"
                    >
                        <Close size="XS" />
                    </button>
                )}
                <span className="submitFormHelpSlot">
                    <ContextualHelp variant="info" UNSAFE_className="submitFormHelp">
                        <Heading>What can I import?</Heading>
                        <Content>
                            <Text>We accept Commander-legal decks hosted on:</Text>
                            <ul className="submitFormHelpList">
                                {SUPPORTED_SITES.map(({ label }) => (
                                    <li key={label}>{label}</li>
                                ))}
                            </ul>
                        </Content>
                    </ContextualHelp>
                </span>
            </div>

            <button
                type="button"
                className="submitFormSubmit"
                onClick={handleSubmit}
                disabled={isDisabled}
                aria-label="Submit deck for analysis"
            >
                <span className="submitFormSubmitShaker" aria-hidden="true" />
                <span className="submitFormSubmitLabel">{isImporting ? 'Seasoning…' : 'Get salty!'}</span>
                {isImporting && (
                    <ProgressCircle
                        size="S"
                        isIndeterminate
                        aria-label="Importing"
                        UNSAFE_className="submitFormSubmitSpinner"
                    />
                )}
            </button>
        </div>
    );
}
